import { useEffect } from 'react';
import { WorkCardPage } from '../pages/WorkCardPage';

interface WorkCardModalProps {
  open: boolean;
  workstationId: string;
  workOrderId: string;
  onClose: () => void;
}

// Fullscreen overlay that hosts the existing WorkCardPage. The standalone
// /work-cards route still works exactly as before — this modal is additive.
// Revert path: delete this component and remove the trigger that mounts it.
export default function WorkCardModal({ open, workstationId, workOrderId, onClose }: WorkCardModalProps) {
  // ESC closes the modal — workers expect it on tablets with keyboards
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-3xl bg-gray-950 rounded-2xl border border-gray-800 shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: 'calc(100vh - 24px)' }}
      >
        {/* Compact modal header — replaces the in-page header/nav of WorkCardPage */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800 flex-shrink-0">
          <span className="text-sm text-gray-400 font-medium">Work Card</span>
          <button
            onClick={onClose}
            aria-label="Close work card"
            className="w-9 h-9 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 text-xl flex items-center justify-center"
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          <WorkCardPage
            modalWsId={workstationId}
            modalWoId={workOrderId}
            onClose={onClose}
          />
        </div>
      </div>
    </div>
  );
}
